import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config';
import { getOrCreateDeviceId } from '../../utils/auth-helpers';
import { validatePassword } from '../../shared/passwordPolicy';  // single source (#consistency P2/E)
import { AuthContext } from '../../App';
import { useTenant } from '../../contexts/TenantContext';
import PreLaunchModal from './PreLaunchModal';

/**
 * Login flow with Phase 2 MFA state machine.
 *
 * States:
 *   creds            → email + password form
 *   mfa_setup_pick   → first-login: user chooses TOTP / SMS / Email
 *   mfa_setup_totp   → QR code + secret + verify input
 *   mfa_setup_code   → SMS/Email code input (post-setup-send)
 *   mfa_setup_done   → show backup codes once
 *   mfa_challenge    → existing user: enter TOTP/SMS/Email code
 *   mfa_backup       → enter a backup code instead
 *   verify_legacy    → existing account-verification OTP flow (unverified users)
 */
function LoginForm({ onToggleMode, onBetaSignup }) {
  const tenant = useTenant();
  const {
    login,
    authenticateWithTokens,
    sessionExpiredReason,
    clearSessionExpiredReason,
  } = React.useContext(AuthContext);

  const [state, setState] = useState('creds');
  const [email, setEmail] = useState(() => localStorage.getItem('healist_remembered_email') || '');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem('healist_remembered_email'));
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [preLaunchMode, setPreLaunchMode] = useState(false);
  const [showPreLaunchModal, setShowPreLaunchModal] = useState(false);
  // #83 — arrived from a beta INVITE email (healist.ai/?invite=1). An invited person
  // must never be shown "Coming Soon / Join the Waitlist" — we already told them
  // they're in. This flips the gate into invited mode and opens it for them, so they
  // are not left guessing whether to press Sign In or Sign Up.
  const [invitedArrival] = useState(() => {
    const hit = new URLSearchParams(window.location.search).get('invite') === '1';
    if (hit) {
      // CONSUME the param immediately. Left in the URL it re-triggers the gate on every
      // later render — so after a user finished signing up and verifying, the beta modal
      // reappeared on top of them. One-shot: read it, strip it, never fire again.
      const url = new URL(window.location.href);
      url.searchParams.delete('invite');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    }
    return hit;
  });

  // Check pre-launch status on mount (Prod only — healist.ai / www.healist.ai)
  useEffect(() => {
    const host = window.location.hostname;
    const isMainDomain = host === 'healist.ai' || host === 'www.healist.ai';
    if (!isMainDomain) return;
    fetch(`${API_BASE}/api/auth/pre-launch-status`)
      .then(r => r.json())
      .then(d => {
        if (d.pre_launch_mode) {
          setPreLaunchMode(true);
          if (invitedArrival) setShowPreLaunchModal(true);   // land them in the right place
        } else if (invitedArrival) {
          onToggleMode();            // gate is off — go straight to the signup form
        }
      })
      .catch(() => {});
  }, [invitedArrival]);   // eslint-disable-line react-hooks/exhaustive-deps
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  // MFA-flow state
  const [tempToken, setTempToken] = useState(null);
  const [mfaMethod, setMfaMethod] = useState(null);
  const [mfaBackupOnly, setMfaBackupOnly] = useState(false);
  const [mfaSetupInfo, setMfaSetupInfo] = useState(null);
  const [selectedMfaMethod, setSelectedMfaMethod] = useState('totp');
  const [mfaCode, setMfaCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);

  // Legacy registration verification
  const [unverifiedUser, setUnverifiedUser] = useState(null);
  const [verificationCodes, setVerificationCodes] = useState({ email_code: '', sms_code: '' });
  const [verifyLaterMessage, setVerifyLaterMessage] = useState(false);

  const resetError = () => setError('');

  // X-Device-Id MUST be sent on every auth call that can create a session (MFA
  // verify-setup / verify / backup-code). Without it the backend defaults the session's
  // device_id to "unknown", which then never matches the real device id on subsequent
  // requests → /logout, inactivity revoke, and all session-bound HIPAA controls silently
  // no-op. (#43 Prod finding 2026-06-11.)
  const bearerHeaders = (token) => ({
    'Content-Type': 'application/json',
    'X-Device-Id': getOrCreateDeviceId(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  });

  // -------------------------------------------------------------------------
  // Step 1 — email + password submit
  // -------------------------------------------------------------------------
  const handleCredsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    resetError();
    if (clearSessionExpiredReason) clearSessionExpiredReason();

    // Remember Me: save or clear email in localStorage (client-side only, no session extension)
    if (rememberMe) {
      localStorage.setItem('healist_remembered_email', email);
    } else {
      localStorage.removeItem('healist_remembered_email');
    }

    const result = await login(email, password);

    if (result.success) {
      // Login complete — AuthContext stored tokens and will fetch the user
      // profile via useEffect. No action needed here.
    } else if (result.must_change_password) {
      setTempToken(result.temp_token);
      setError(result.reason);
      setState('force_password_change');
    } else if (result.requires_mfa_setup) {
      setTempToken(result.temp_token);
      setState('mfa_setup_pick');
    } else if (result.requires_mfa) {
      setTempToken(result.temp_token);
      setMfaMethod(result.mfa_method);
      setMfaBackupOnly(result.backup_only);
      setState(result.backup_only ? 'mfa_backup' : 'mfa_challenge');
    } else if (result.locked) {
      setError(`${result.error} (until ${result.locked_until})`);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // -------------------------------------------------------------------------
  // MFA Setup — pick method, enrol, verify first code
  // -------------------------------------------------------------------------
  const handlePickMethod = async (method) => {
    setLoading(true);
    resetError();
    try {
      const resp = await fetch(`${API_BASE}/api/auth/mfa/setup`, {
        method: 'POST',
        headers: bearerHeaders(tempToken),
        body: JSON.stringify({ method }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setError(data.detail || 'Setup failed');
        setLoading(false);
        return;
      }
      setMfaMethod(method);
      setMfaSetupInfo(data);
      setState(method === 'totp' ? 'mfa_setup_totp' : 'mfa_setup_code');
    } catch (e) {
      setError('Network error starting MFA setup');
    }
    setLoading(false);
  };

  const handleVerifySetup = async () => {
    setLoading(true);
    resetError();
    try {
      const resp = await fetch(`${API_BASE}/api/auth/mfa/verify-setup`, {
        method: 'POST',
        headers: bearerHeaders(tempToken),
        body: JSON.stringify({ code: mfaCode }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setError(typeof data.detail === 'object'
          ? `Invalid code — ${data.detail.attempts || 0} attempt(s). ${data.detail.locked_until ? 'Locked until ' + data.detail.locked_until : ''}`
          : (data.detail || 'Verification failed'));
        setMfaCode('');
        setLoading(false);
        return;
      }
      // /mfa/verify-setup response contains access_token + refresh_token +
      // backup_codes. Stash the tokens for the "Continue" button after the
      // user has saved their backup codes.
      pendingTokens.current = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      };
      setBackupCodes(data.backup_codes || []);
      setState('mfa_setup_done');
    } catch (e) {
      setError('Network error verifying code');
    }
    setLoading(false);
  };

  const pendingTokens = React.useRef(null);

  // -------------------------------------------------------------------------
  // MFA Challenge — existing user enters their code
  // -------------------------------------------------------------------------
  const handleVerifyChallenge = async () => {
    setLoading(true);
    resetError();
    try {
      const resp = await fetch(`${API_BASE}/api/auth/mfa/verify`, {
        method: 'POST',
        headers: bearerHeaders(tempToken),
        body: JSON.stringify({ code: mfaCode }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        if (data.detail && data.detail.locked) {
          setError(`Account locked. ${data.detail.locked_until ? 'Try again after ' + new Date(data.detail.locked_until).toLocaleTimeString() : ''}`);
        } else if (data.detail && data.detail.backup_only) {
          setMfaBackupOnly(true);
          setState('mfa_backup');
          setError(data.detail.message || 'Use a backup code to unlock');
        } else {
          setError('Invalid code. Please try again.');
        }
        setMfaCode('');
        setLoading(false);
        return;
      }
      await authenticateWithTokens(data);   // clears the previous session's cache first (D11)
    } catch (e) {
      setError('Network error verifying code');
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setLoading(true);
    resetError();
    try {
      const resp = await fetch(`${API_BASE}/api/auth/mfa/resend`, {
        method: 'POST',
        headers: bearerHeaders(tempToken),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setError(data.detail?.message || data.detail || 'Resend failed');
      } else {
        setError('');
        alert('A new code has been sent.');
      }
    } catch (e) {
      setError('Resend failed');
    }
    setLoading(false);
  };

  // -------------------------------------------------------------------------
  // Backup Code Flow
  // -------------------------------------------------------------------------
  const handleBackupCode = async () => {
    setLoading(true);
    resetError();
    try {
      const resp = await fetch(`${API_BASE}/api/auth/mfa/backup-code`, {
        method: 'POST',
        headers: bearerHeaders(tempToken),
        body: JSON.stringify({ code: mfaCode }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setError('Invalid backup code.');
        setMfaCode('');
        setLoading(false);
        return;
      }
      await authenticateWithTokens(data);   // clears the previous session's cache first (D11)
    } catch (e) {
      setError('Network error using backup code');
    }
    setLoading(false);
  };

  const handleCancelMfa = () => {
    setTempToken(null);
    setMfaMethod(null);
    setMfaSetupInfo(null);
    setMfaCode('');
    setMfaBackupOnly(false);
    setState('creds');
    resetError();
  };

  // -------------------------------------------------------------------------
  // Legacy account-verification flow (existing code path for unverified users)
  // -------------------------------------------------------------------------
  const handleVerifyLegacy = async () => {
    setLoading(true);
    resetError();
    try {
      const response = await fetch(`${API_BASE}/api/auth/verify-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: unverifiedUser.email,
          phone: unverifiedUser.phone,
          email_code: verificationCodes.email_code,
          sms_code: verificationCodes.sms_code,
        }),
      });
      const result = await response.json();
      if (result.success) {
        // User is now verified — they're already logged in via tokens
        setState('creds');
        setUnverifiedUser(null);
      } else {
        setError(result.message);
      }
    } catch (e) {
      setError('Verification failed. Please try again.');
    }
    setLoading(false);
  };

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------
  const renderMfaSetupPick = () => (
    <div className="login-form">
      <h2 style={{ textAlign: 'center', marginBottom: 8 }}>Set Up Two-Factor Authentication</h2>
      <p style={{ textAlign: 'center', color: 'var(--brand-text-muted, #6B7280)', marginBottom: 24, fontSize: 14 }}>
        Two-factor authentication is required to protect your account. Choose a method:
      </p>
      {error && <div className="alert alert-error">{error}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <button type="button" className={selectedMfaMethod === 'totp' ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setSelectedMfaMethod('totp')} disabled={loading}>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontWeight: 600 }}>Authenticator App (recommended)</div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>Google Authenticator, Authy, 1Password, etc.</div>
          </div>
        </button>
        <button type="button" className={selectedMfaMethod === 'sms' ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setSelectedMfaMethod('sms')} disabled={loading}>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontWeight: 600 }}>Text Message (SMS)</div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>6-digit code sent to your verified phone</div>
          </div>
        </button>
        <button type="button" className={selectedMfaMethod === 'email' ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setSelectedMfaMethod('email')} disabled={loading}>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontWeight: 600 }}>Email</div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>6-digit code sent to your verified email</div>
          </div>
        </button>
        <button type="button" className="btn btn-primary" onClick={() => handlePickMethod(selectedMfaMethod)} disabled={loading || !selectedMfaMethod} style={{ marginTop: 16 }}>
          {loading ? 'Setting up...' : 'Continue'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={handleCancelMfa} disabled={loading}>
          Cancel
        </button>
      </div>
    </div>
  );

  const renderMfaSetupTotp = () => (
    <div className="login-form">
      <h2 style={{ textAlign: 'center', marginBottom: 8 }}>Scan this QR Code</h2>
      <p style={{ textAlign: 'center', color: 'var(--brand-text-muted, #6B7280)', marginBottom: 16, fontSize: 14 }}>
        Open your authenticator app and scan the code below.
      </p>
      {error && <div className="alert alert-error">{error}</div>}
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        {mfaSetupInfo?.qr_code && <img src={mfaSetupInfo.qr_code} alt="TOTP QR Code" width="200" height="200" />}
      </div>
      {mfaSetupInfo?.secret && (
        <>
          <p style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Manual entry key:</p>
          <code style={{ display: 'block', padding: 8, background: '#f5f5f5', borderRadius: 6, fontSize: 13, wordBreak: 'break-all', textAlign: 'center', userSelect: 'all', marginBottom: 16 }}>
            {mfaSetupInfo.secret}
          </code>
        </>
      )}
      <div className="form-group">
        <label>6-digit code from your authenticator</label>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]{6}"
          maxLength={6}
          value={mfaCode}
          onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
          placeholder="000000"
          autoFocus
        />
      </div>
      <button type="button" className="btn btn-primary" onClick={handleVerifySetup} disabled={loading || mfaCode.length !== 6}>
        {loading ? <span className="spinner"></span> : 'Verify & Enable 2FA'}
      </button>
      <button type="button" className="btn btn-secondary" onClick={handleCancelMfa} disabled={loading} style={{ marginTop: 8 }}>
        Back
      </button>
    </div>
  );

  const renderMfaSetupCode = () => (
    <div className="login-form">
      <h2 style={{ textAlign: 'center', marginBottom: 8 }}>Enter Verification Code</h2>
      <p style={{ textAlign: 'center', color: 'var(--brand-text-muted, #6B7280)', marginBottom: 16, fontSize: 14 }}>
        We sent a 6-digit code to <strong>{mfaSetupInfo?.destination_hint || 'your account'}</strong>.
        Valid for 5 minutes.
      </p>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="form-group">
        <label>6-digit code</label>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]{6}"
          maxLength={6}
          value={mfaCode}
          onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
          placeholder="000000"
          autoFocus
        />
      </div>
      <button type="button" className="btn btn-primary" onClick={handleVerifySetup} disabled={loading || mfaCode.length !== 6}>
        {loading ? <span className="spinner"></span> : 'Verify & Enable 2FA'}
      </button>
      <button type="button" className="btn btn-secondary" onClick={handleCancelMfa} disabled={loading} style={{ marginTop: 8 }}>
        Back
      </button>
    </div>
  );

  const renderMfaSetupDone = () => (
    <div className="login-form">
      <h2 style={{ textAlign: 'center', marginBottom: 8 }}>Save Your Backup Codes</h2>
      <p style={{ textAlign: 'center', color: 'var(--color-error, #DC2626)', marginBottom: 16, fontSize: 14, fontWeight: 600 }}>
        Save these codes now — they won't be shown again. Each can be used once if you lose your device.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 16 }}>
        {backupCodes.map((code, i) => (
          <code key={i} style={{ padding: 8, background: '#f5f5f5', borderRadius: 4, textAlign: 'center', fontSize: 14 }}>
            {code}
          </code>
        ))}
      </div>
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => {
          // /mfa/verify-setup returned full tokens in the response body; they
          // were captured into pendingTokens by handleVerifySetup. Fall back
          // to a silent re-login if the ref is empty (edge case).
          if (pendingTokens.current) {
            authenticateWithTokens(pendingTokens.current);
          } else {
            // Retry with password (user is about to log in anyway).
            login(email, password).then(() => {
              setState('creds');
            });
          }
        }}
      >
        I've saved my codes — Continue
      </button>
    </div>
  );

  const renderMfaChallenge = () => (
    <div className="login-form">
      <h2 style={{ textAlign: 'center', marginBottom: 8 }}>Two-Factor Authentication</h2>
      <p style={{ textAlign: 'center', color: 'var(--brand-text-muted, #6B7280)', marginBottom: 16, fontSize: 14 }}>
        {mfaMethod === 'totp' && 'Enter the 6-digit code from your authenticator app.'}
        {mfaMethod === 'sms' && 'We sent a 6-digit code to your phone.'}
        {mfaMethod === 'email' && 'We sent a 6-digit code to your email.'}
      </p>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="form-group">
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]{6}"
          maxLength={6}
          value={mfaCode}
          onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
          placeholder="000000"
          autoFocus
          style={{ fontSize: 24, textAlign: 'center', letterSpacing: '0.5em' }}
        />
      </div>
      <button type="button" className="btn btn-primary" onClick={handleVerifyChallenge} disabled={loading || mfaCode.length !== 6}>
        {loading ? <span className="spinner"></span> : 'Verify'}
      </button>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: 13 }}>
        {(mfaMethod === 'sms' || mfaMethod === 'email') && (
          <a href="#" onClick={(e) => { e.preventDefault(); handleResend(); }}>Resend code</a>
        )}
        <a href="#" onClick={(e) => { e.preventDefault(); setMfaCode(''); setState('mfa_backup'); resetError(); }}>
          Use a backup code
        </a>
      </div>
      <button type="button" className="btn btn-secondary" onClick={handleCancelMfa} disabled={loading} style={{ marginTop: 16 }}>
        Cancel
      </button>
    </div>
  );

  const renderMfaBackup = () => (
    <div className="login-form">
      <h2 style={{ textAlign: 'center', marginBottom: 8 }}>Enter a Backup Code</h2>
      <p style={{ textAlign: 'center', color: 'var(--brand-text-muted, #6B7280)', marginBottom: 16, fontSize: 14 }}>
        Use one of the one-time backup codes you saved when enabling 2FA.
      </p>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="form-group">
        <input
          type="text"
          inputMode="numeric"
          value={mfaCode}
          onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
          placeholder="8-digit backup code"
          autoFocus
          style={{ fontSize: 20, textAlign: 'center' }}
        />
      </div>
      <button type="button" className="btn btn-primary" onClick={handleBackupCode} disabled={loading || mfaCode.length !== 8}>
        {loading ? <span className="spinner"></span> : 'Use Backup Code'}
      </button>
      {!mfaBackupOnly && (
        <button type="button" className="btn btn-secondary" onClick={() => { setMfaCode(''); setState('mfa_challenge'); resetError(); }} disabled={loading} style={{ marginTop: 8 }}>
          Back to code entry
        </button>
      )}
      <button type="button" className="btn btn-secondary" onClick={handleCancelMfa} disabled={loading} style={{ marginTop: 8 }}>
        Cancel
      </button>
    </div>
  );

  // -------------------------------------------------------------------------
  // Forced password change (retroactive policy enforcement)
  // -------------------------------------------------------------------------
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwChangeError, setPwChangeError] = useState('');

  const handleForcePasswordChange = async () => {
    setPwChangeError('');
    if (!validatePassword(newPw)) {
      setPwChangeError('Password must be at least 12 characters with uppercase, lowercase, digit, and special character.');
      return;
    }
    if (newPw !== confirmPw) {
      setPwChangeError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/api/auth/force-change-password`, {
        method: 'POST',
        headers: bearerHeaders(tempToken),
        body: JSON.stringify({
          new_password: newPw,
          confirm_password: confirmPw,
        }),
      });
      if (resp.ok) {
        setNewPw('');
        setConfirmPw('');
        setError('');
        setState('creds');
        setPassword('');
        alert('Password updated. Please sign in with your new password.');
      } else {
        const data = await resp.json().catch(() => ({}));
        setPwChangeError(data.detail || 'Password change failed.');
      }
    } catch (e) {
      setPwChangeError('Network error. Please try again.');
    }
    setLoading(false);
  };

  const renderForcePasswordChange = () => (
    <div className="login-form">
      <h2 style={{ textAlign: 'center', marginBottom: 8 }}>Update Your Password</h2>
      <p style={{ textAlign: 'center', color: 'var(--brand-text-muted, #6B7280)', marginBottom: 16, fontSize: 14 }}>
        {error || 'Your password does not meet our updated security requirements.'}
      </p>
      {pwChangeError && <div className="alert alert-error">{pwChangeError}</div>}
      <div style={{ fontSize: 13, color: 'var(--brand-text-muted)', marginBottom: 16, padding: '12px', background: 'var(--brand-surface-elevated, #f8f9fc)', borderRadius: 8 }}>
        <strong>Requirements:</strong>
        <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
          <li style={{ color: newPw.length >= 12 ? 'var(--color-success, #059669)' : 'inherit' }}>At least 12 characters</li>
          <li style={{ color: /[A-Z]/.test(newPw) ? 'var(--color-success)' : 'inherit' }}>One uppercase letter</li>
          <li style={{ color: /[a-z]/.test(newPw) ? 'var(--color-success)' : 'inherit' }}>One lowercase letter</li>
          <li style={{ color: /\d/.test(newPw) ? 'var(--color-success)' : 'inherit' }}>One digit</li>
          <li style={{ color: /[!@#$%^&*()_+\-=\[\]{}|;:'".,<>?/`~\\]/.test(newPw) ? 'var(--color-success)' : 'inherit' }}>One special character</li>
        </ul>
      </div>
      <div className="form-group">
        <label>New Password</label>
        <div className="pw-field-wrap">
          <input type={showNewPw ? 'text' : 'password'} value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Enter new password" />
          <button type="button" className="pw-eye-btn" onClick={() => setShowNewPw(!showNewPw)} aria-label="Toggle password visibility">{showNewPw ? '🙈' : '👁'}</button>
        </div>
      </div>
      <div className="form-group">
        <label>Confirm New Password</label>
        <div className="pw-field-wrap">
          <input type={showConfirmPw ? 'text' : 'password'} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder="Confirm new password" />
          <button type="button" className="pw-eye-btn" onClick={() => setShowConfirmPw(!showConfirmPw)} aria-label="Toggle password visibility">{showConfirmPw ? '🙈' : '👁'}</button>
        </div>
      </div>
      <button type="button" className="btn btn-primary" onClick={handleForcePasswordChange}
        disabled={loading || !validatePassword(newPw) || newPw !== confirmPw}>
        {loading ? <span className="spinner"></span> : 'Update Password'}
      </button>
      <button type="button" className="btn btn-secondary" onClick={() => { setState('creds'); setError(''); setNewPw(''); setConfirmPw(''); }}
        style={{ marginTop: 8 }} disabled={loading}>
        Cancel
      </button>
    </div>
  );

  const renderCreds = () => (
    <form className="login-form" onSubmit={handleCredsSubmit}>
      {sessionExpiredReason && (
        <div className={`alert ${sessionExpiredReason === 'revoked' ? 'alert-error' : 'alert-warning'} login-session-banner`}>
          {sessionExpiredReason === 'revoked'
            ? 'You were logged out from another device. Please sign in again if this was unexpected.'
            : sessionExpiredReason === 'inactivity'
              ? 'You were signed out due to inactivity. Please sign in again.'
              : 'Your session has expired. Please sign in again.'}
        </div>
      )}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="form-group">
        <label>Email Address</label>
        <input type="email" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      </div>

      <div className="form-group">
        <label>Password</label>
        <div className="pw-field-wrap">
          <input type={showPassword ? 'text' : 'password'} placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          <button type="button" className="pw-eye-btn" onClick={() => setShowPassword(!showPassword)} aria-label="Toggle password visibility">{showPassword ? '🙈' : '👁'}</button>
        </div>
      </div>

      <div className="form-row">
        <label><input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} /> Remember me</label>
        <a href="#" onClick={(e) => { e.preventDefault(); setForgotEmail(email || ''); setForgotSent(false); setShowForgotModal(true); }}>Forgot password?</a>
      </div>

      <button type="submit" className="btn btn-primary" disabled={loading}>
        {loading ? <span className="spinner"></span> : 'Sign In'}
      </button>

      <div className="login-divider">Other Options</div>

      <button type="button" className="btn-secondary-signup" onClick={() => {
        if (preLaunchMode) { setShowPreLaunchModal(true); return; }
        onToggleMode();
      }}>Sign Up</button>

      {window.location.hostname.startsWith('dev') && (
        <div className="login-rate-limits-link">
          <a href="/rate-limits">Pre-Revenue Rate Limits for Application Testing Users</a>
        </div>
      )}

      <div className="login-footer-links">
        <a href={tenant.about_url || '/healist-about.html'}><strong>About {tenant.site_name}</strong></a>
        <a href={tenant.privacy_url || '/privacy-policy.html'}>Privacy Policy</a>
        <a href={tenant.terms_url || '/terms-of-service.html'}>Terms & Conditions</a>
        {/* Security & privacy posture. Deliberately reachable WITHOUT an account — the people who
            most need it are deciding whether to sign up. WL: a partner's own page if they set one,
            otherwise shown only on Healist — /security.html is Healist-branded and would misrepresent
            a partner's domain. */}
        {(tenant.security_url || tenant.is_default_tenant) &&
          <a href={tenant.security_url || '/security.html'}>Security</a>}
        {tenant.is_default_tenant && <a href="/media-kit/">Press & Media</a>}
        {tenant.is_default_tenant && <a href="/healist-investors.html">Investors</a>}
      </div>

      <div className="login-copyright">
        {tenant.copyright_text || <><span>&copy; 2026 Pivotal Technologies, LLC</span><br />Precision AI Health Technology&trade;</>}<br />
        <strong>Patent Pending</strong> — All rights reserved.
      </div>
    </form>
  );

  return (
    <>
      <div className="login-page">
        <div className="login-brand-panel">
          <div className="brand-content">
            <div className="brand-logo">
              <div className="brand-mark">
                <img src={tenant.logo_url || '/icon-96x96.png'} alt={tenant.site_name} />
              </div>
              <div className="brand-wordmark">
                {tenant.is_default_tenant ? <>Healist<span>.AI</span></> : tenant.site_name}
              </div>
            </div>
            <h1 className="brand-headline">{tenant.site_tagline || 'Precision AI Health Technology\u2122'}</h1>
            <p className="brand-description">
              Experience the convergence of medical expertise and artificial intelligence.
              Comprehensive health management designed for those who demand excellence.
            </p>
            <div className="brand-credentials">
              <div className="credential"><div className="credential-icon">🔒</div><div className="credential-text">HIPAA Compliant</div></div>
              <div className="credential"><div className="credential-icon">🏥</div><div className="credential-text">Clinical Grade</div></div>
              <div className="credential"><div className="credential-icon">🧠</div><div className="credential-text">AI Powered</div></div>
            </div>
          </div>
        </div>

        <div className="login-form-panel">
          <div className="login-header">
            <div className="login-logo">
              <img src={tenant.logo_url || '/healist-logo.png'} alt={`${tenant.site_name} Logo`} />
            </div>
            <div className="login-header-text">
              <h2>Welcome to {tenant.site_name}</h2>
              <p>Precision AI Health Technology™</p>
            </div>
          </div>

          {state === 'creds' && renderCreds()}
          {state === 'force_password_change' && renderForcePasswordChange()}
          {state === 'mfa_setup_pick' && renderMfaSetupPick()}
          {state === 'mfa_setup_totp' && renderMfaSetupTotp()}
          {state === 'mfa_setup_code' && renderMfaSetupCode()}
          {state === 'mfa_setup_done' && renderMfaSetupDone()}
          {state === 'mfa_challenge' && renderMfaChallenge()}
          {state === 'mfa_backup' && renderMfaBackup()}
        </div>
      </div>

      {/* Legacy registration-verification modal (pre-MFA code path for unverified users) */}
      {state === 'verify_legacy' && !verifyLaterMessage && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3 className="modal-title">Account Verification</h3>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-group">
              <label>SMS verification code</label>
              <input type="text" placeholder={`sent to ${unverifiedUser?.phone}`} value={verificationCodes.sms_code}
                onChange={(e) => setVerificationCodes((p) => ({ ...p, sms_code: e.target.value }))} maxLength={6} />
            </div>
            <div className="form-group">
              <label>Email verification code</label>
              <input type="text" placeholder={`sent to ${unverifiedUser?.email}`} value={verificationCodes.email_code}
                onChange={(e) => setVerificationCodes((p) => ({ ...p, email_code: e.target.value }))} maxLength={6} />
            </div>
            <div className="modal-actions">
              <button type="button" onClick={handleVerifyLegacy} disabled={loading || !verificationCodes.email_code || !verificationCodes.sms_code} className="btn btn-primary">Verify</button>
              <button type="button" onClick={() => setVerifyLaterMessage(true)} disabled={loading} className="btn btn-secondary">Verify Later</button>
              <button type="button" onClick={async () => { await fetch(`${API_BASE}/api/auth/resend-codes?user_id=${unverifiedUser.id}`, { method: 'POST' }); }} disabled={loading} className="btn btn-secondary">Resend Codes</button>
              <button type="button" onClick={() => setState('creds')} disabled={loading} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {verifyLaterMessage && (
        <div className="modal-overlay">
          <div className="modal-content">
            <p className="modal-text">
              The verification codes sent will be active for 2 hours only. If you do not verify within that time period you will need to click the 'Resend Codes' button from the Account Verification form.
            </p>
            <div className="modal-actions">
              <button type="button" onClick={() => { setVerifyLaterMessage(false); setState('creds'); setUnverifiedUser(null); }} className="btn-primary">OK</button>
              <button type="button" onClick={() => setVerifyLaterMessage(false)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Forgot Password Modal (D4S) */}
      {showForgotModal && (
        <div className="modal-overlay forgot-pw-modal" onClick={() => !loading && setShowForgotModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {!forgotSent ? (
              <>
                <h3 className="modal-title">Reset Your Password</h3>
                <p className="modal-subtitle">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="Enter your email"
                    autoFocus
                  />
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={loading || !forgotEmail}
                    onClick={async () => {
                      setLoading(true);
                      try {
                        await fetch(`${API_BASE}/api/auth/forgot-password`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ email: forgotEmail }),
                        });
                        setForgotSent(true);
                      } catch (_) {
                        setForgotSent(true);
                      }
                      setLoading(false);
                    }}
                  >
                    {loading ? <span className="spinner"></span> : 'Send Reset Link'}
                  </button>
                  <button type="button" className="btn btn-secondary" disabled={loading}
                    onClick={() => setShowForgotModal(false)}>
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="modal-title">Check Your Inbox</h3>
                <p className="modal-subtitle">
                  If there is an account associated with <strong>{forgotEmail}</strong>, a password
                  reset link has been sent. The link expires in 15 minutes.
                </p>
                <p className="modal-subtitle">
                  Don't see it? Check your spam folder.
                </p>
                <div className="modal-footer modal-footer-center">
                  <button type="button" className="btn btn-primary"
                    onClick={() => { setShowForgotModal(false); setForgotSent(false); }}>
                    Back to Sign In
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Pre-Launch Modal */}
      {showPreLaunchModal && (
        <PreLaunchModal
          invited={invitedArrival}
          onClose={() => setShowPreLaunchModal(false)}
          onBetaAllowed={(email) => {
            setShowPreLaunchModal(false);
            if (onBetaSignup) onBetaSignup(email);   // #83 → switch to the real register form
          }}
        />
      )}
    </>
  );
}

export default LoginForm;
